import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { usePlan } from "../context/PlanContext";
import { Share2, X, Lock, Send, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

/**
 * "Post my scan setup" — Executive-only. Opens a small modal that lets the
 * user add a headline/commentary + pick a community, then submits the current
 * filters + top 5 hits to /api/community/post-scan-setup. Server-side does
 * the formatting so the post looks consistent across users.
 */
const COMMUNITY_OPTIONS = [
  { slug: "strategies", label: "r/Strategies" },
  { slug: "day-trading", label: "r/DayTrading" },
  { slug: "swing-trading", label: "r/SwingTrading" },
  { slug: "options", label: "r/Options" },
  { slug: "stocks", label: "r/Stocks" },
];

function toSharePayload(filters, results) {
  const topHits = (results || []).slice(0, 5).map((r) => ({
    ticker: r.ticker,
    price: r.price,
    chg_pct: r.chg_pct,
    score: r.score,
    rsi: r.indicators?.rsi,
    rvol: r.indicators?.rvol,
  }));
  return {
    filters: {
      assetClasses: filters.market ? [filters.market] : [],
      assetTypes: filters.asset_types || [],
      timeframe: filters.timeframe,
      indicators: filters.indicators || [],
      strength: filters.signal_strength,
      direction: filters.direction || "any",
    },
    top_hits: topHits,
  };
}

export default function ShareScanButton({ filters, results }) {
  const { plan, showUpgrade } = usePlan();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState("");
  const [commentary, setCommentary] = useState("");
  const [community, setCommunity] = useState("strategies");
  const gated = plan !== "executive";
  const hasResults = (results?.length || 0) > 0;

  const click = () => {
    if (gated) { showUpgrade("share_gated"); return; }
    if (!hasResults) { toast.error("Run a scan first so there's something to share"); return; }
    setOpen(true);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const base = toSharePayload(filters, results);
      const { data } = await api.post("/api/community/post-scan-setup", {
        ...base,
        title: title.trim(),
        commentary: commentary.trim(),
        community_slug: community,
      });
      toast.success(data.mod_status === "reduced_visibility"
        ? "Shared with reduced visibility (under review)"
        : "Shared to the community!");
      setOpen(false);
      setTitle(""); setCommentary("");
      navigate(`/community/post/${data.id}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(d?.message || d || "Share failed");
    } finally { setBusy(false); }
  };

  const topPreview = (results || []).slice(0, 5);

  return (
    <>
      <button
        onClick={click}
        className={`btn-ghost inline-flex items-center gap-2 ${gated ? "opacity-70" : ""}`}
        title={gated ? "Sharing scan setups is an Executive feature" : "Post my scan setup to the community"}
        data-testid="share-scan-btn"
      >
        {gated ? <Lock className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
        <span className="hidden sm:inline">Share scan</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-2xl flex items-center justify-center p-4 fade-in"
          onClick={() => setOpen(false)}
          data-testid="share-scan-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="card-surface w-full max-w-lg p-5 sm:p-6 relative overflow-hidden"
          >
            <div className="absolute inset-0 pointer-events-none opacity-60"
                 style={{ background: "radial-gradient(circle at 100% 0%, rgba(0,255,156,0.14), transparent 55%)" }} />
            <button onClick={() => setOpen(false)} aria-label="Close"
                    className="absolute top-4 right-4 text-white/50 hover:text-white p-1"
                    data-testid="share-scan-close">
              <X className="w-4 h-4" />
            </button>
            <div className="relative">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-3.5 h-3.5 text-[var(--bull)]" />
                <div className="label-mono">Share to community</div>
              </div>
              <h2 className="text-white text-xl font-semibold tracking-tight">
                Post my scan setup
              </h2>
              <p className="text-white/60 text-[13px] mt-1 leading-relaxed">
                We'll share your current filters + top 5 hits as a
                formatted post. You get a self-upvote and karma for the post.
              </p>

              <div className="mt-4 space-y-2.5">
                <div>
                  <label className="label-mono">Community</label>
                  <select value={community} onChange={(e) => setCommunity(e.target.value)}
                          className="input-glass w-full mt-1"
                          data-testid="share-community-select">
                    {COMMUNITY_OPTIONS.map((c) => (
                      <option key={c.slug} value={c.slug}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label-mono">Title (optional)</label>
                  <input value={title} onChange={(e) => setTitle(e.target.value)}
                         placeholder={topPreview[0]
                           ? `My scan setup — top hit: $${topPreview[0].ticker}`
                           : "My FlowFinder scan setup"}
                         className="input-glass w-full mt-1" maxLength={200}
                         data-testid="share-title-input"/>
                </div>
                <div>
                  <label className="label-mono">Commentary (optional)</label>
                  <textarea value={commentary} onChange={(e) => setCommentary(e.target.value)}
                            rows={3} maxLength={1000}
                            placeholder="Why did you build this setup? What are you watching for?"
                            className="input-glass w-full mt-1"
                            data-testid="share-commentary-input"/>
                </div>

                {topPreview.length > 0 && (
                  <div className="mt-1 rounded-xl border border-white/10 bg-white/[0.03] p-3">
                    <div className="label-mono mb-1.5">Preview — top {topPreview.length} hits</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                      {topPreview.map((h) => (
                        <div key={h.ticker}
                             className="flex items-center justify-between gap-1 px-2 py-1 rounded-lg bg-black/20 border border-white/5">
                          <span className="text-white text-[12px] font-semibold font-mono">${h.ticker}</span>
                          <span className="label-mono">{h.score ?? "—"}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-5 flex items-center justify-end gap-2">
                <button onClick={() => setOpen(false)} className="btn-ghost"
                        data-testid="share-cancel-btn">
                  Cancel
                </button>
                <button onClick={submit} disabled={busy}
                        className="btn-accent inline-flex items-center gap-2"
                        data-testid="share-submit-btn">
                  <Send className="w-3.5 h-3.5" />
                  {busy ? "Sharing…" : "Share to community"}
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
